import { useMemo } from 'react';
import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { useShallow } from 'zustand/react/shallow';
import { useProductsList } from '../hooks';
import { selectProductQuery, useProductFilters } from '../store/filters.store';
import { ProductStatusBadge } from './ProductStatusBadge';
import { ProductActionsDropdown } from './ProductActionsDropdown';
import { TableSkeleton } from '@/src/modules/components/ui/LoadingSkeleton';
import { EmptyState, ErrorState } from '@/src/modules/components/ui/EmptyState';
import { formatPrice } from '@/src/infrastructure/lib/currency';
import type { AdminProductRow } from '../types';

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function ProductTable() {
  const query = useProductFilters(useShallow(selectProductQuery));
  const { data, isLoading, isError, error, refetch } = useProductsList(query);

  const columns = useMemo<ColumnDef<AdminProductRow>[]>(
    () => [
      {
        accessorKey: 'displayName',
        header: 'Product',
        cell: ({ row }) => (
          <div>
            <div className="font-medium text-sm leading-tight">{row.original.displayName}</div>
            <div className="text-[10px] text-on-surface-variant">{row.original.code}</div>
          </div>
        ),
      },
      {
        accessorKey: 'template',
        header: 'Template',
        cell: ({ row }) => <span className="text-sm">{row.original.template.name}</span>,
      },
      {
        accessorKey: 'amountInPaise',
        header: 'Price',
        cell: ({ row }) => (
          <span className="text-sm">{formatPrice(row.original.amountInPaise, row.original.currency)}</span>
        ),
      },
      {
        // Issue #435 (Phase 2): tier tag column
        accessorKey: 'tier',
        header: 'Tier',
        cell: ({ row }) =>
          row.original.tier ? (
            <span className="text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-electric-violet/10 text-electric-violet">
              {row.original.tier}
            </span>
          ) : (
            <span className="text-xs text-on-surface-variant/50">—</span>
          ),
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        cell: ({ row }) => <ProductStatusBadge isActive={row.original.isActive} />,
      },
      {
        accessorKey: 'displayOrder',
        header: 'Order',
        cell: ({ row }) => <span className="text-sm">{row.original.displayOrder}</span>,
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => <span className="text-xs text-on-surface-variant">{fmtDate(row.original.createdAt)}</span>,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => <ProductActionsDropdown product={row.original} />,
      },
    ],
    [],
  );

  const table = useReactTable({
    data: data?.items ?? [],
    columns,
    // Without this, rows are keyed by array index — pagination/refetch then
    // silently reuses row component instances across different products,
    // which is exactly the class of bug the stale-`ProductPriceEditor`
    // default-values fix above depends on rows being correctly identified.
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) return <TableSkeleton cols={7} />;
  if (isError) return <ErrorState message={(error as Error).message} onRetry={() => void refetch()} />;
  if (!data || data.items.length === 0) {
    return <EmptyState title="No products match these filters" hint="Try clearing filters or widening the search." />;
  }

  return (
    <div className="glass-panel rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-surface-container-low">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th key={h.id} className="px-3 py-2 text-[10px] uppercase tracking-widest font-bold text-on-surface-variant">
                    {flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="border-t border-border-subtle hover:bg-white/5">
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-3 py-2">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination total={data.meta.total} page={data.meta.page} totalPages={data.meta.totalPages} />
    </div>
  );
}

function Pagination({ page, totalPages, total }: { page: number; totalPages: number; total: number }) {
  const filters = useProductFilters();
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-border-subtle text-xs text-on-surface-variant">
      <span>
        Page {page} of {totalPages || 1} ({total} total)
      </span>
      <div className="flex gap-2">
        <button
          disabled={page <= 1}
          onClick={() => filters.setFilter('page', page - 1)}
          className="px-3 py-1.5 rounded-md border border-border-subtle disabled:opacity-40 hover:bg-white/5"
        >
          Prev
        </button>
        <button
          disabled={page >= (totalPages || 1)}
          onClick={() => filters.setFilter('page', page + 1)}
          className="px-3 py-1.5 rounded-md border border-border-subtle disabled:opacity-40 hover:bg-white/5"
        >
          Next
        </button>
      </div>
    </div>
  );
}
